import { css } from '@emotion/react';
import { Button, Flex, Modal, Stack, Text, Textarea } from '@inflearn/ds-react';
import { useEffect, useState } from 'react';
import { useGamesResultsResultIdUpdateMutation } from '../../../../mutations/useGamesResultsResultIdUpdateMutation';
import type { GameResultModalProps } from './types';

export const GameResultModal = ({
  isOpen,
  score,
  gameType,
  gameResult,
  resultId,
  onRestart,
  onCommentSubmit,
}: GameResultModalProps) => {
  const [isCommentMode, setIsCommentMode] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: updateComment } = useGamesResultsResultIdUpdateMutation({
    resultId: resultId ?? 0,
  });

  // 모달이 열릴 때마다 초기화
  useEffect(() => {
    if (isOpen) {
      setIsCommentMode(false);
      setComment('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleCommentSubmit = () => {
    if (!resultId || !comment.trim()) {
      return;
    }

    setIsSubmitting(true);
    updateComment(
      {
        comment: comment.trim(),
      },
      {
        onSuccess: () => {
          setIsSubmitting(false);
          onCommentSubmit();
          onRestart(); // 모달 닫기
        },
        onError: () => {
          setIsSubmitting(false);
          alert('코멘트 저장에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  const getModalTitle = () => {
    if (isCommentMode) {
      return '소감 한마디';
    }
    if (gameType === 'GET_OFF_WORK' && gameResult === 'lose') {
      return 'GAME OVER';
    }
    return '🎉 게임 성공!';
  };

  const getModalBody = () => {
    // 점수 표시 포맷 계산 (GET_OFF_WORK는 밀리초 → 초, 나머지는 점수)
    const displayScore =
      gameType === 'GET_OFF_WORK' ? (score / 1000).toFixed(2) : score.toLocaleString();
    const scoreUnit = gameType === 'GET_OFF_WORK' ? '초' : '점';

    if (isCommentMode) {
      return (
        <Stack spacing="md" w="100%">
          <Stack spacing={0}>
            <Text css={finalScoreTextStyle}>
              {displayScore}
              {scoreUnit}
            </Text>
          </Stack>
          <Stack spacing="xs">
            <Textarea
              placeholder="인프런 짱!"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              minRows={4}
              maxRows={8}
              maxLength={200}
            />
            <Text size="xs" c="gray.5">
              {comment.length} / 200자
            </Text>
          </Stack>
          <Text size="xs" c="gray.6">
            • 문구는 수정할 수 없으니 신중하게 작성해주세요.
            <br />• 스팸, 욕설 등 부적절한 내용은 제재를 받을 수 있습니다.
          </Text>
        </Stack>
      );
    }

    if (gameType === 'GET_OFF_WORK' && gameResult === 'lose') {
      return (
        <Stack>
          <img src={`${process.env.NEXT_PUBLIC_CDN_URL}/public/games/h_laser.png`} alt="향로 빔" />
          <Text size={48} color="red.5" fw={700} ta="center">
            어디가세요?
          </Text>
        </Stack>
      );
    }

    return (
      <>
        <Text size="md" c="gray.7">
          최종 {gameType === 'GET_OFF_WORK' ? '기록' : '점수'}
        </Text>
        <Text css={finalScoreTextStyle}>
          {displayScore}
          {scoreUnit}
        </Text>
      </>
    );
  };

  return (
    <Modal.Root opened={isOpen} onClose={onRestart} size="md" closeOnClickOutside={false}>
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{getModalTitle()}</Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          <Stack spacing={0} align="center" py="xl">
            {getModalBody()}
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          {isCommentMode ? (
            <Flex gap="sm" w="100%">
              <Button
                size="md"
                variant="outline"
                onClick={() => setIsCommentMode(false)}
                disabled={isSubmitting}
                css={{ flexGrow: 1 }}
              >
                취소
              </Button>
              <Button
                size="md"
                onClick={handleCommentSubmit}
                css={{ flexGrow: 1 }}
                disabled={!comment.trim() || isSubmitting}
                loading={isSubmitting}
              >
                저장
              </Button>
            </Flex>
          ) : (
            <Flex gap="sm" w="100%">
              <Button size="md" variant="default" onClick={onRestart} css={{ flexGrow: 1 }}>
                공부하러가기
              </Button>
              <Button
                size="md"
                variant="filled"
                onClick={() => setIsCommentMode(true)}
                disabled={isSubmitting || !resultId}
                css={{ flexGrow: 1 }}
              >
                소감 남기기
              </Button>
            </Flex>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

// Styles
const finalScoreTextStyle = css`
  font-size: 48px;
  font-weight: bold;
  background: linear-gradient(135deg, #4caf50 0%, #81c784 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;
